import { getReportVariable } from "@common/helpers/report-variables";
import { parseTokenString } from "@common/helpers/variable-field";
import {
    ReportSettingKey,
    ReportSettingValues,
} from "@common/helpers/report-settings/report-setting-fields";
import { ReportSettingSource } from "@common/helpers/report-settings/report-setting-source";
import { WorkspaceReportSettingsResponse } from "@common/schemas/domains/reportSettings/workspace-report-settings.schema";
import { ReportSettingLogoOption } from "@common/types/reports/report-settings";
import { ReportSettingsReportKey } from "@common/types/reports/report-settings-report-key";
import { ROW_VISIBILITY_OPTIONS } from "./layout/layoutControls";
import {
    AMOUNT_SCALE_OPTIONS,
    CURRENCY_PLACEMENT_OPTIONS,
    CURRENCY_SYMBOL_OPTIONS,
    DECIMAL_PLACES_OPTIONS,
    NEGATIVE_FORMAT_OPTIONS,
    PERCENT_DECIMAL_PLACES_OPTIONS,
    ZERO_DISPLAY_OPTIONS,
    labelFor,
} from "./numberFormat/numberFormatControls";
import { REPORT_SETTINGS_REPORT_OPTIONS } from "./reportTypeOptions";

export const FIELD_NOUNS: Record<ReportSettingKey, string> = {
    header: "header",
    title: "title",
    subtitle: "subtitle",
    subtitle2: "subtitle 2",
    footer: "footer",
    memo: "memo",
    watermarkText: "watermark",
    logoOption: "logo",
    rowVisibility: "account visibility",
    decimalPlaces: "decimal places",
    negativeFormat: "negative numbers",
    isNegativeInRed: "red negatives",
    hasThousandsSeparator: "thousands separator",
    zeroDisplay: "zero amounts",
    currencySymbol: "currency symbol",
    currencyPlacement: "currency symbol placement",
    percentDecimalPlaces: "percentage decimals",
    amountScale: "amount scale",
};

const LOGO_LABELS: Record<ReportSettingLogoOption, string> = {
    [ReportSettingLogoOption.NONE]: "No logo",
    [ReportSettingLogoOption.FIRM]: "Firm logo",
    [ReportSettingLogoOption.ENTITY]: "Entity logo",
    [ReportSettingLogoOption.BOTH]: "Both",
};

export const reportLabel = (reportKey: ReportSettingsReportKey): string =>
    labelFor(REPORT_SETTINGS_REPORT_OPTIONS, reportKey);

export type TemplateNames = Record<string, string | undefined>;

export const getTemplateNames = ({
    entities,
    workspaceTemplate,
    firmDefaultTemplate,
}: WorkspaceReportSettingsResponse): TemplateNames =>
    Object.fromEntries([
        ...[
            workspaceTemplate,
            ...entities.map(({ template }) => template),
        ].flatMap((applied) =>
            applied?.id && applied.name ? [[applied.id, applied.name]] : [],
        ),
        ...(firmDefaultTemplate
            ? [[firmDefaultTemplate.id, firmDefaultTemplate.name]]
            : []),
    ]);

const templateNameOf = (
    source: ReportSettingSource,
    templateNames: TemplateNames,
): string | undefined =>
    source.templateId ? templateNames[source.templateId] : undefined;

export const describeResetTitle = ({
    source,
    templateNames,
}: {
    source: ReportSettingSource;
    templateNames: TemplateNames;
}): string => {
    switch (source.kind) {
        case "template":
            return "Reset to template default";
        case "entity-override":
            return "Reset to entity default";
        case "workspace-override":
            return "Reset to client default";
        case "entity-template":
        case "workspace-template":
        case "firm-default-template": {
            const templateName = templateNameOf(source, templateNames);
            return templateName
                ? `Reset to ${templateName} template`
                : "Reset to template";
        }
        case "kick-default":
            return "Reset to Kick default";
        default: {
            const exhaustiveCheck: never = source.kind;
            return exhaustiveCheck;
        }
    }
};

export type ResetValuePart =
    | { kind: "text"; text: string }
    | { kind: "variable"; label: string };

const isReportVariableKey = (key: string) =>
    getReportVariable(key) !== undefined;

const toTextParts = (value: string): ResetValuePart[] =>
    value.trim() === ""
        ? [{ kind: "text", text: "Empty" }]
        : parseTokenString(value, isReportVariableKey).map(
              (segment): ResetValuePart =>
                  segment.kind === "text"
                      ? { kind: "text", text: segment.value }
                      : {
                            kind: "variable",
                            label:
                                getReportVariable(segment.variableKey)?.label ??
                                segment.variableKey,
                        },
          );

const toLabelParts = (text: string): ResetValuePart[] => [
    { kind: "text", text },
];
const toSwitchParts = (value: boolean): ResetValuePart[] =>
    toLabelParts(value ? "On" : "Off");

const RESET_VALUE_PARTS: {
    [K in ReportSettingKey]: (
        value: ReportSettingValues[K],
    ) => ResetValuePart[];
} = {
    header: toTextParts,
    title: toTextParts,
    subtitle: toTextParts,
    subtitle2: toTextParts,
    footer: toTextParts,
    memo: toTextParts,
    watermarkText: toTextParts,
    logoOption: (value) => toLabelParts(LOGO_LABELS[value]),
    rowVisibility: (value) =>
        toLabelParts(labelFor(ROW_VISIBILITY_OPTIONS, value)),
    decimalPlaces: (value) =>
        toLabelParts(labelFor(DECIMAL_PLACES_OPTIONS, value)),
    negativeFormat: (value) =>
        toLabelParts(labelFor(NEGATIVE_FORMAT_OPTIONS, value)),
    isNegativeInRed: toSwitchParts,
    hasThousandsSeparator: toSwitchParts,
    zeroDisplay: (value) => toLabelParts(labelFor(ZERO_DISPLAY_OPTIONS, value)),
    currencySymbol: (value) =>
        toLabelParts(labelFor(CURRENCY_SYMBOL_OPTIONS, value)),
    currencyPlacement: (value) =>
        toLabelParts(labelFor(CURRENCY_PLACEMENT_OPTIONS, value)),
    percentDecimalPlaces: (value) =>
        toLabelParts(labelFor(PERCENT_DECIMAL_PLACES_OPTIONS, value)),
    amountScale: (value) => toLabelParts(labelFor(AMOUNT_SCALE_OPTIONS, value)),
};

export const describeResetValue = <K extends ReportSettingKey>({
    field,
    value,
}: {
    field: K;
    value: ReportSettingValues[K];
}): ResetValuePart[] => RESET_VALUE_PARTS[field](value);

export const resetValueToText = (parts: ResetValuePart[]): string =>
    parts
        .map((part) => (part.kind === "text" ? part.text : part.label))
        .join("");
